import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../environments/environment';
import { PaginatedCollection } from '../app/models/PaginatedCollection';
import {
  Catalog,
  CatalogMaterial,
  CatalogMaterialsQuery,
  CatalogRequest,
  CatalogUploadPlan,
  Collection
} from '../app/models/catalog';

/**
 * The global material catalogs a tenant can import from, as the admin maintains them.
 *
 * Tenant-less: a catalog belongs to nobody until a tenant imports from it.
 *
 * The upload requests carry a FormData body, so they must NOT set Content-Type: the browser has to
 * write it itself to include the multipart boundary, or the backend will not find the file part.
 */
@Injectable({
  providedIn: 'root'
})
export class CatalogService {
  private readonly catalogsUrl = `${environment.api.baseUrl}${environment.api.endpoints.catalogs}`;

  constructor(private http: HttpClient) {}

  getCatalogs(): Observable<Collection<Catalog>> {
    return this.http.get<Collection<Catalog>>(this.catalogsUrl);
  }

  getCatalog(id: number | string): Observable<Catalog> {
    return this.http.get<Catalog>(`${this.catalogsUrl}/${id}`);
  }

  createCatalog(request: CatalogRequest): Observable<Catalog> {
    return this.http.post<Catalog>(this.catalogsUrl, request);
  }

  updateCatalog(id: number | string, request: CatalogRequest): Observable<Catalog> {
    return this.http.put<Catalog>(`${this.catalogsUrl}/${id}`, request);
  }

  publish(id: number | string): Observable<Catalog> {
    return this.http.post<Catalog>(`${this.catalogsUrl}/${id}/publish`, null);
  }

  archive(id: number | string): Observable<Catalog> {
    return this.http.post<Catalog>(`${this.catalogsUrl}/${id}/archive`, null);
  }

  getMaterials(id: number | string, params: CatalogMaterialsQuery = {}): Observable<PaginatedCollection<CatalogMaterial>> {
    const query = new URLSearchParams();
    query.set('page', String(params.page ?? 0));
    query.set('size', String(params.size ?? 25));
    if (params.search && params.search.trim() !== '') {
      query.set('search', params.search.trim());
    }
    if (params.category) {
      query.set('category', params.category);
    }
    return this.http.get<PaginatedCollection<CatalogMaterial>>(
      `${this.catalogsUrl}/${id}/materials?${query.toString()}`);
  }

  /** Reads the CSV and reports what uploading it would do. Writes nothing. */
  previewUpload(id: number | string, file: File): Observable<CatalogUploadPlan> {
    return this.http.post<CatalogUploadPlan>(`${this.catalogsUrl}/${id}/upload/preview`, this.body(file));
  }

  /** Applies the CSV and bumps the catalog's version. Refused (400) if any row is in error. */
  upload(id: number | string, file: File): Observable<CatalogUploadPlan> {
    return this.http.post<CatalogUploadPlan>(`${this.catalogsUrl}/${id}/upload`, this.body(file));
  }

  /**
   * The CSV template, as a Blob. Fetched through HttpClient rather than linked, because a plain
   * <a href> would go out without the bearer token and be refused.
   */
  downloadTemplate(): Observable<Blob> {
    return this.http.get(`${this.catalogsUrl}/template.csv`, { responseType: 'blob' });
  }

  private body(file: File): FormData {
    const form = new FormData();
    form.append('file', file, file.name);
    return form;
  }
}
